import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from './SEO';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import FloatingActions from './layout/FloatingActions';
import ImageSlider from "./ui/ImageSlider";
import { seoConfig } from '../data/seoData';
import { elevareFAQs } from '../data/faqsData';
import { elevareSkills, elevareCourses, elevareFirstYearPrograms } from '../data/coursesData';
import useFavicon from '../hooks/useFavicon';
import LazyImage from './ui/LazyImage';
import LazyVideo from './ui/LazyVideo';

// Bespoke premium rotating words animation for the Elevare Hero Heading
function RotatingHeading() {
  const words = ["Career Skills", "Tech Portfolio", "Engineering Path", "Future Placements"];
  const [wordIdx, setWordIdx] = useState(0);
  const [subText, setSubText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let timer;
    const currentWord = words[wordIdx];
    const typingSpeed = isDeleting ? 40 : 85;

    if (isDeleting) {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length - 1));
      }, typingSpeed);
    } else {
      timer = setTimeout(() => {
        setSubText(currentWord.substring(0, subText.length + 1));
      }, typingSpeed);
    }

    if (!isDeleting && subText === currentWord) {
      timer = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && subText === "") {
      setIsDeleting(false);
      setWordIdx((prev) => (prev + 1) % words.length);
    }

    return () => clearTimeout(timer);
  }, [subText, isDeleting, wordIdx]);

  return (
    <span className="inline-block relative py-1 bg-gradient-to-r from-[#00BFFF] via-[#1E90FF] to-[#0B1C2D] bg-clip-text text-transparent font-extrabold tracking-tight min-w-[150px] min-[360px]:min-w-[240px] sm:min-w-[320px] md:min-w-[420px] lg:min-w-[460px]">
      {subText}
      <span className="border-r-[3.5px] border-[#0B1C2D] ml-1.5 animate-pulse" style={{ verticalAlign: 'middle' }}></span>
    </span>
  );
}

export default function Elevare() {
  useFavicon('/elevare academy-favicon/web-app-manifest-512x512.png');
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const toggleFAQ = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  // Card cursor spotlight tracking handler
  const handleMouseMove = (e) => {
    const { currentTarget, clientX, clientY } = e;
    const rect = currentTarget.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    currentTarget.style.setProperty("--mouse-x", `${x}px`);
    currentTarget.style.setProperty("--mouse-y", `${y}px`);
  };

  const filteredCourses = elevareCourses.filter(course => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'now') return course.available;
    if (selectedFilter === 'soon') return !course.available;
    return true;
  });

  // Syllabus Timeline Roadmap data
  const syllabusPath = [
    { phase: "Phase 1", title: "Web Basics Foundation", desc: "HTML5 semantic syntax, CSS3 flexbox/grid layout structures, responsive web designs, and basic JavaScript DOM logic." },
    { phase: "Phase 2", title: "Modern Frontends", desc: "Building modular single-page web applications with React.js, managing router states, hooks, and clean Tailwind styling." },
    { phase: "Phase 3", title: "Backend Systems", desc: "Setting up Node.js runtimes, Express.js server instances, crafting RESTful APIs, and implementing CORS controllers." },
    { phase: "Phase 4", title: "Database & Cloud Deploy", desc: "Designing MongoDB database schemas, handling secure CRUD, environmental variables, and deploying live production builds." }
  ];

  return (
    <React.Fragment>
      <SEO {...seoConfig.elevare} faqData={elevareFAQs} />

      <div className="font-inter bg-nm-light min-h-screen text-[#1E293B]">
        {/* Unified sticky nav header */}
        <Navbar />

        {/* Hero Section */}
        <section className="relative overflow-hidden w-full min-h-[90vh] flex items-center py-20 lg:py-24 bg-nm-light text-[#0B1C2D]" aria-label="Hero Section">
          {/* Responsive Brand Video Background */}
          <LazyVideo
            src={isMobile ? "/videos/elevare-mobile.mp4" : "/videos/elevare-desktop.mp4"}
            poster="/elevare-logo.webp"
            className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.25]"
            placeholderBg="bg-transparent"
          />

          {/* Background glows */}
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#00BFFF]/10 rounded-full blur-[130px] animate-sunrise-shift pointer-events-none" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#1E90FF]/5 rounded-full blur-[120px] animate-pulse-slow pointer-events-none" style={{ animationDelay: '4s' }} />

          {/* grid lines overlay */}
          <div
            className="absolute inset-0 opacity-[0.03] pointer-events-none"
            style={{
              backgroundImage: "linear-gradient(to right, #00BFFF 1px, transparent 1px), linear-gradient(to bottom, #00BFFF 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />

          <div className="relative max-w-[1500px] mx-auto px-6 w-full flex flex-col items-center justify-center min-h-[80vh] relative z-20 py-20">
            {/* Centered Content Column */}
            <div className="relative z-20 text-center max-w-4xl mx-auto flex flex-col items-center animate-slide-up">
              {/* Header Badge with Logo */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <LazyImage
                  src="/elevare academy-favicon/apple-touch-icon.png"
                  alt="Elevare Logo"
                  className="w-14 h-14 rounded-full object-cover border border-[#00BFFF]/20 shadow-lg bg-white"
                  fetchPriority="high"
                />
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-light text-[#0B1C2D] text-xs font-semibold">
                  <span className="w-2 h-2 rounded-full bg-[#00BFFF] animate-pulse" />
                  <span>College Students & Young Professionals</span>
                </div>
              </div>

              {/* Centered Heading */}
              <div className="flex flex-col items-center gap-4 mb-6">
                <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] text-[#0B1C2D] tracking-tight">
                  <span className="block">Build Your</span>
                  <span className="block mt-2"><RotatingHeading /></span>
                </h1>
              </div>

              {/* Centered Description */}
              <p className="text-slate-600 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10 font-light">
                Upskill with industry-aligned full-stack engineering and analytics tracks, build a verified portfolio, and prepare for placement rounds.
              </p>

              {/* Centered CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 w-full max-w-md">
                <a
                  href="/book-demo?vertical=elevare"
                  className="px-8 py-4.5 rounded-2xl glass-button-light text-[#00BFFF] font-bold text-center w-full sm:w-auto"
                >
                  Book ₹99 Demo Session (7-Days)
                </a>
                <a
                  href="#skills"
                  className="px-8 py-4.5 rounded-2xl glass-button-light text-[#0B1C2D] font-bold text-center w-full sm:w-auto"
                >
                  View Curriculum
                </a>
              </div>

              {/* Centered stats */}
              <div className="flex flex-wrap justify-center gap-6 w-full">
                {["50+ Skills", "1-on-1 Mentors", "95% Readiness"].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 glass-card-light px-5 py-3 rounded-2xl hover:scale-105 transition-all duration-300">
                    <div className="w-8 h-8 rounded-full nm-inset-light flex items-center justify-center text-[#00BFFF] font-bold text-sm">✓</div>
                    <span className="text-sm font-semibold text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= RELATABLE MARQUEE loop ================= */}
        <section className="bg-nm-light border-y border-slate-200/40 py-8 relative overflow-hidden" aria-label="Elevare Trust Indicators">
          <div className="max-w-7xl mx-auto px-6 mb-4 text-center">
            <span className="text-xs font-bold text-[#00BFFF] uppercase tracking-widest">Our Engineering & Placement Excellence</span>
          </div>

          <div className="relative flex overflow-hidden w-full select-none py-2">
            {/* First Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop">
              {elevareMarquee.map((logo, idx) => (
                <div key={idx} className="flex items-center gap-3 text-slate-500 hover:text-[#0B1C2D] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
            {/* Second Track */}
            <div className="flex shrink-0 gap-16 justify-around min-w-full animate-marquee-loop" aria-hidden="true">
              {elevareMarquee.map((logo, idx) => (
                <div key={`dup-${idx}`} className="flex items-center gap-3 text-slate-500 hover:text-[#0B1C2D] transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    {logo.svg}
                  </div>
                  <span className="text-sm font-semibold uppercase tracking-wider">{logo.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Career Skills Section */}
        <section id="skills" className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Core <span className="bg-gradient-to-r from-[#0B1C2D] to-sky-500 bg-clip-text text-transparent">Career Skills</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Industry-demanded competencies that form our standard training curriculum.
              </p>
            </div>

            {/* Cursor spotlight animated cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {elevareSkills.map((item, index) => (
                <div
                  key={index}
                  onMouseMove={handleMouseMove}
                  className="spotlight-card relative nm-flat-light p-8 rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-300 flex flex-col justify-between"
                >
                  <div>
                    <div className="w-14 h-14 rounded-2xl nm-inset-light flex items-center justify-center text-3xl mb-6">
                      {item.icon}
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-[#0B1C2D]">{item.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light">{item.description}</p>
                  </div>
                  <ul className="space-y-2 border-t border-slate-200/30 pt-4">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-slate-700 text-xs font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E] mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ₹99 7-Day Demo Experience Section */}
        <section id="demo-experience" className="py-24 bg-nm-light border-b border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="nm-flat-light rounded-3xl p-8 md:p-12 border-transparent relative overflow-hidden flex flex-col lg:flex-row items-center gap-12">
              {/* Background glows */}
              <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#00BFFF]/10 rounded-full blur-[80px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-[#1E90FF]/5 rounded-full blur-[60px] pointer-events-none" />

              <div className="lg:w-1/2 relative z-10">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00BFFF]/10 border border-[#00BFFF]/20 text-[#00BFFF] text-xs font-bold uppercase tracking-wider mb-6">
                  <span className="w-2 h-2 rounded-full bg-[#00BFFF] animate-pulse" />
                  Limited Time Demo Access
                </span>
                <h2 className="font-playfair text-3xl md:text-5xl font-bold text-[#0B1C2D] leading-tight mb-6">
                  Experience the Entire Course in <span className="bg-gradient-to-r from-[#00BFFF] to-[#1E90FF] bg-clip-text text-transparent">7 Days</span>
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-8 font-light">
                  Why commit before you try? Fill out the demo form, and for the payment of <strong className="text-[#0B1C2D] font-semibold">₹99</strong>, contact Cervello Elevare Academy to get complete access to our structured curriculum path, interactive mentoring platform, and build a real-world project.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/book-demo?vertical=elevare"
                    className="px-8 py-4.5 rounded-2xl nm-button-light text-[#00BFFF] font-bold border-transparent text-center"
                  >
                    Start 7-Day Trial (₹99)
                  </Link>
                  <a
                    href="#skills"
                    className="px-8 py-4.5 rounded-2xl nm-button-light text-[#0B1C2D] font-bold border-transparent text-center"
                  >
                    View Curriculum
                  </a>
                </div>
              </div>

              <div className="lg:w-1/2 w-full grid sm:grid-cols-2 gap-6 relative z-10">
                {/* Demo Feature Card 1 */}
                <div className="nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl nm-inset-light flex items-center justify-center text-[#00BFFF] text-2xl mb-4">📅</div>
                    <h3 className="font-bold text-[#0B1C2D] text-lg mb-2">7-Day Course Condense</h3>
                    <p className="text-slate-500 text-xs leading-relaxed font-light">
                      Experience the entire multi-month course duration structure compressed into an immersive 7-day learning cycle.
                    </p>
                  </div>
                </div>

                {/* Demo Feature Card 2 */}
                <div className="nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl nm-inset-light flex items-center justify-center text-amber-500 text-2xl mb-4">🛠️</div>
                    <h3 className="font-bold text-[#0B1C2D] text-lg mb-2">Mini Project Dev</h3>
                    <p className="text-slate-500 text-xs leading-relaxed font-light">
                      Don't just listen—build. Construct, refine, and deploy a functional mini-project within the demo week.
                    </p>
                  </div>
                </div>

                {/* Demo Feature Card 3 */}
                <div className="nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl nm-inset-light flex items-center justify-center text-emerald-500 text-2xl mb-4">👥</div>
                    <h3 className="font-bold text-[#0B1C2D] text-lg mb-2">Live Support & Sync</h3>
                    <p className="text-slate-500 text-xs leading-relaxed font-light">
                      Interact with our dedicated course advisors, join cohort discussion groups, and resolve code syntax blockers.
                    </p>
                  </div>
                </div>

                {/* Demo Feature Card 4 */}
                <div className="nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between">
                  <div>
                    <div className="w-12 h-12 rounded-xl nm-inset-light flex items-center justify-center text-purple-500 text-2xl mb-4">💻</div>
                    <h3 className="font-bold text-[#0B1C2D] text-lg mb-2">LMS Portal Access</h3>
                    <p className="text-slate-500 text-xs leading-relaxed font-light">
                      Get hands-on with our learning platform, look over sandbox environments, and review session notes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tab-Filtered Courses Section */}
        <section className="py-24 bg-nm-light border-y border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Featured <span className="bg-gradient-to-r from-[#0B1C2D] to-[#C6A25E] bg-clip-text text-transparent">Courses</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base mb-10">
                Explore our dynamic curriculum models with flexible availability options.
              </p>

              {/* Bespoke Category Filter Tabs */}
              <div className="inline-flex p-1.5 nm-inset-light rounded-2xl mb-12">
                {[
                  { id: 'all', label: 'All Subjects' },
                  { id: 'now', label: 'Available Now' },
                  { id: 'soon', label: 'Coming Soon' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedFilter(tab.id)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer ${selectedFilter === tab.id
                      ? 'nm-flat-light text-[#00BFFF] font-bold border-transparent'
                      : 'text-slate-500 hover:text-[#0B1C2D]'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Courses grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, index) => (
                <div
                  key={index}
                  className="group nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col justify-between h-full"
                >
                  <Link to={`/course/${getSlug(course.title)}`} className="relative aspect-video w-full overflow-hidden bg-slate-100 block">
                    <ImageSlider
                      images={course.images}
                      alt={course.title}
                      className="w-full h-full"
                    />
                    <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md shadow-sm ${course.available
                      ? 'bg-emerald-500/95 text-white z-20'
                      : 'bg-amber-500/95 text-white z-20'
                      }`}>
                      {course.status}
                    </span>
                  </Link>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">CERVELLO ELEVARE</span>
                      <Link to={`/course/${getSlug(course.title)}`}>
                        <h3 className="text-xl font-bold text-[#0B1C2D] mt-1 mb-2 hover:text-[#C6A25E] transition-colors">{course.title}</h3>
                      </Link>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light line-clamp-2">{course.description}</p>
                    </div>

                    <div>
                      <ul className="space-y-2 mb-6 nm-inset-light p-4 rounded-2xl">
                        {course.techs.map((tech, idx) => (
                          <li key={idx} className="flex items-center text-slate-700 text-xs font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#00BFFF] mr-2 flex-shrink-0" />
                            {tech}
                          </li>
                        ))}
                      </ul>

                      {course.available ? (
                        <Link
                          to={`/course/${getSlug(course.title)}`}
                          className="w-full nm-button-light py-3 rounded-xl font-semibold border-transparent text-center block"
                        >
                          View Course Details
                        </Link>
                      ) : (
                        <button
                          className="w-full bg-slate-100 text-slate-400 py-3 rounded-xl font-semibold cursor-not-allowed"
                          disabled
                        >
                          Coming Soon
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Visual Syllabus Path (Roadmap) */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Our Learning <span className="bg-gradient-to-r from-[#00BFFF] to-[#1E90FF] bg-clip-text text-transparent">Pathway</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                A structured roadmap showing standard checkpoints in our coding classes.
              </p>
            </div>

            <div className="relative max-w-3xl mx-auto border-l-2 border-[#00BFFF]/30 pl-8 space-y-12">
              {syllabusPath.map((step, idx) => (
                <div key={idx} className="relative group">
                  {/* timeline dot indicator */}
                  <div className="absolute -left-[41px] top-1.5 w-6 h-6 rounded-full bg-white border-4 border-[#00BFFF] group-hover:scale-110 transition-transform duration-300 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#1E90FF]" />
                  </div>

                  <div className="nm-flat-light p-6 rounded-2xl border-transparent hover:scale-[1.01] transition-all duration-300">
                    <span className="text-xs font-bold text-[#00BFFF] tracking-widest uppercase">{step.phase}</span>
                    <h3 className="text-lg font-bold text-[#0B1C2D] mt-1 mb-2">{step.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed font-light">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* First-Year Specific Programs */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Special <span className="bg-gradient-to-r from-[#C6A25E] to-[#D4AF37] bg-clip-text text-transparent">First-Year</span> Programs
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Foundational classes designed to introduce younger college students to syntax models.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {elevareFirstYearPrograms.map((program, index) => (
                <div
                  key={index}
                  className="group nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col justify-between h-full"
                >
                  <Link to={`/course/${getSlug(program.title)}`} className="relative aspect-video w-full overflow-hidden bg-slate-100 block">
                    <ImageSlider
                      images={program.images}
                      alt={program.title}
                      className="w-full h-full"
                    />
                    <span className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md shadow-sm bg-gradient-to-tr from-[#C6A25E] to-[#D4AF37] text-white z-20">
                      {program.status || "First Year Program"}
                    </span>
                  </Link>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">FOUNDATIONAL MODULE</span>
                      <Link to={`/course/${getSlug(program.title)}`}>
                        <h3 className="text-xl font-bold text-[#0B1C2D] mt-1 mb-2 hover:text-[#C6A25E] transition-colors">{program.title}</h3>
                      </Link>
                      <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light line-clamp-2">{program.description}</p>
                    </div>

                    <div>
                      <ul className="space-y-1.5 mb-6 border-t border-slate-200/30 pt-4">
                        {program.techs.map((tech, idx) => (
                          <li key={idx} className="flex items-center text-slate-700 text-xs font-semibold">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E] mr-2 flex-shrink-0" />
                            {tech}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={`/course/${getSlug(program.title)}`}
                        className="w-full nm-button-light py-3 rounded-xl font-semibold border-transparent text-center block"
                      >
                        View Course Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Guest Lecture & Industry Interaction Section */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B1C2D]/5 border border-[#0B1C2D]/10 text-[#0B1C2D] text-xs font-semibold uppercase tracking-wider mb-4">
                <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
                Industry Networking
              </span>
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Guest Lecture <span className="bg-gradient-to-r from-[#0B1C2D] to-sky-500 bg-clip-text text-transparent">Series</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Engage in live interaction sessions with engineering leaders, successfully placed alumni, and tech specialists.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Card 1: Industry Veterans */}
              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center justify-center px-3 py-1 rounded-full nm-inset-light text-blue-500 text-xs font-bold mb-6">
                    Industry Experts & Leads
                  </div>
                  <div className="text-3xl mb-4">🏢</div>
                  <h3 className="text-xl font-bold text-[#0B1C2D] mb-3">Tech Architects & Staff Engineers</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light">
                    Deep dives into system architectures at scale, production infrastructure, microservices, and AI integrations directly from veterans at global tech leaders.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200/30">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00BFFF]" />
                    Real-world System Designs
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mt-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00BFFF]" />
                    Scalability Case Studies
                  </div>
                </div>
              </div>

              {/* Card 2: Placed Freshers */}
              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center justify-center px-3 py-1 rounded-full nm-inset-light text-emerald-500 text-xs font-bold mb-6">
                    Successfully Placed Alumni
                  </div>
                  <div className="text-3xl mb-4">🎓</div>
                  <h3 className="text-xl font-bold text-[#0B1C2D] mb-3">Recently Placed Freshers</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light">
                    Gain peer perspective from recent graduates who cracked intensive placement drives. Learn about exact coding round questions, resume styling, and placement strategy.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200/30">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Interview Crack Playbooks
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mt-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Portfolio Strategy Reviews
                  </div>
                </div>
              </div>

              {/* Card 3: Senior Developers */}
              <div className="nm-flat-light p-8 rounded-3xl border-transparent hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center justify-center px-3 py-1 rounded-full nm-inset-light text-[#C6A25E] text-xs font-bold mb-6">
                    Experienced Professionals
                  </div>
                  <div className="text-3xl mb-4">💻</div>
                  <h3 className="text-xl font-bold text-[#0B1C2D] mb-3">Senior Devs & Team Leads</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light">
                    Interact with senior professionals about clean code principles, pull request mechanics, Agile workflow dynamics, and developer tool collaboration workflows.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200/30">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E]" />
                    Collaborative Code Reviews
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mt-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C6A25E]" />
                    Daily Engineering Cycles
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= FOUNDER'S VISION SECTION ================= */}
        <section className="py-24 bg-nm-dark text-[#0B1C2D] relative overflow-hidden border-t border-slate-200/20">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000001_1px,transparent_1px),linear-gradient(to_bottom,#00000002_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#00BFFF]/10 rounded-full blur-[120px] pointer-events-none" />

          <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full nm-flat-dark border-transparent shadow-md mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00BFFF] animate-pulse" />
              <span className="text-xs font-semibold tracking-wider text-[#0B1C2D] uppercase">Founder's Vision</span>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-24 h-24 rounded-full overflow-hidden mb-6 border-2 border-[#C6A25E] shadow-xl relative group">
                <LazyImage src="/founder.webp" alt="Mohd. Yaasir Sufiyan" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              </div>

              <blockquote className="text-xl md:text-2xl font-playfair font-medium italic text-slate-800 leading-relaxed max-w-3xl">
                "To bridge the gap between academic theory and industry reality, we must shift our focus from just writing syntax to understanding architectural patterns and solving real-world challenges. At Elevare, we guide aspiring builders to construct production-ready software systems with absolute confidence."
              </blockquote>

              <div className="mt-8">
                <h4 className="text-lg font-bold text-[#C6A25E]">Mohd. Yaasir Sufiyan</h4>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Founder & CEO, Cervello Eduverse</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-24 bg-nm-light border-t border-slate-200/40">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-playfair text-3xl md:text-5xl font-bold mb-4 text-[#0B1C2D]">
                Elevare <span className="bg-gradient-to-r from-[#00BFFF] to-[#1E90FF] bg-clip-text text-transparent">FAQ</span>
              </h2>
              <p className="text-slate-500 max-w-xl mx-auto text-base">
                Everything you need to know about our student and young professional career tracks.
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {elevareFAQs.map((faq, index) => {
                const isOpen = activeFaq === index;
                const hasList = faq.answer && faq.answer.listItems;
                return (
                  <div key={index} className="nm-flat-light rounded-2xl border-transparent overflow-hidden">
                    <button
                      className="w-full text-left p-6 flex justify-between items-center bg-transparent focus:outline-none"
                      onClick={() => toggleFAQ(index)}
                      aria-expanded={isOpen}
                    >
                      <h3 className="font-bold text-base sm:text-lg text-[#0B1C2D]">{faq.question}</h3>
                      <span className={`text-[#00BFFF] text-xl transition-transform duration-300 transform ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
                        ▾
                      </span>
                    </button>

                    <div
                      className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-72 opacity-100 border-t border-slate-200/30' : 'max-h-0 opacity-0 pointer-events-none'
                        }`}
                    >
                      <div className="p-6 text-slate-600 text-sm leading-relaxed nm-inset-light">
                        {hasList ? (
                          <div>
                            <p className="mb-2 font-medium">{faq.answer.text}</p>
                            <ul className="list-disc pl-5 space-y-1">
                              {faq.answer.listItems.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p>{faq.answer}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Unified footer & FABs */}
        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}

// Elevare marquee dataset
const elevareMarquee = [
  {
    name: "Full-Stack Engineering",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )
  },
  {
    name: "Data Analytics & ML",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 002 2h2a2 2 0 002-2" />
      </svg>
    )
  },
  {
    name: "React & Node.js",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    )
  },
  {
    name: "System Architecture",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3zM9 20V4M15 20V4M4 9h16M4 15h16" />
      </svg>
    )
  },
  {
    name: "Interview Prep",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    )
  },
  {
    name: "Placement Readiness",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  },
  {
    name: "Industry Case Labs",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 11.172V5L8 4z" />
      </svg>
    )
  },
  {
    name: "1-on-1 Code Reviews",
    svg: (
      <svg className="w-8 h-8 text-[#00BFFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    )
  }
];
